import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Heart } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useMissions } from '@/hooks/useMissions';
import { 
  likeDeveloper, 
  unlikeDeveloper, 
  isDeveloperLiked, 
  getDeveloperLikesCount 
} from '@/services/likesService';
import { toast } from 'sonner';

interface DeveloperLikeButtonProps {
  developerId: string;
  className?: string;
}

export function DeveloperLikeButton({ developerId, className }: DeveloperLikeButtonProps) {
  const { user } = useAuth();
  const { handleLikeDeveloper } = useMissions();
  const [isLiked, setIsLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.id && developerId) {
      loadLikeStatus();
    }
  }, [user?.id, developerId]);

  const loadLikeStatus = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const [liked, count] = await Promise.all([
        isDeveloperLiked(developerId, user.id),
        getDeveloperLikesCount(developerId)
      ]);
      
      setIsLiked(liked);
      setLikesCount(count);
    } catch (error) {
      console.error('Error loading like status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleLike = async () => {
    if (!user) {
      toast.error('You must be logged in to like developers');
      return;
    }

    try {
      if (isLiked) {
        const success = await unlikeDeveloper(developerId, user.id);
        if (success) {
          setIsLiked(false);
          setLikesCount(prev => Math.max(0, prev - 1));
          toast.success('Developer unliked');
        } else {
          toast.error('Failed to unlike developer');
        }
      } else {
        const success = await likeDeveloper(developerId, user.id);
        if (success) {
          setIsLiked(true);
          setLikesCount(prev => prev + 1);
          toast.success('Developer liked!');
          
          // Complete the like developer mission
          handleLikeDeveloper(developerId);
        } else {
          toast.error('Failed to like developer');
        }
      }
    } catch (error) {
      console.error('Error toggling like:', error);
      toast.error('An error occurred');
    }
  };

  if (loading) {
    return (
      <Button variant="outline" size="sm" disabled className={`h-[19px] px-2 text-[12px] ${className}`}>
        <Heart className="w-4 h-4 -mr-1" />
        Loading...
      </Button>
    );
  }

  return (
    <Button
      variant={isLiked ? "default" : "outline"}
      size="sm"
      onClick={handleToggleLike}
      className={`h-[19px] px-2 text-[12px] ${className}`}
    >
      <Heart 
        className={`w-4 h-4 -mr-1 ${isLiked ? 'fill-current' : ''}`} 
      />
      {likesCount}
    </Button>
  );
}
